import React from 'react';
import moment from 'moment';
import lodashTimes from 'lodash/fp/times';
import lodashMean from 'lodash/fp/mean';
import lodashValues from 'lodash/fp/values';
import slugify from 'common/util/slugify';
import makeFullName from 'common/util/makeFullName';
import addNumberPronounSuffix from 'common/util/addNumberPronounSuffix';
import typeCheck from 'common/util/typeCheck';
import undefinedToNull from 'common/util/undefinedToNull';
import {
  roundTwoDecimals,
} from 'common/formatters';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF,
  getLatestStatus,
} from 'common/applicationAssemblyLineConstants';

import {
  pickAssemblyLineApplicationOfferLetterFields,
} from 'server/models/AssemblyLineApplication/fieldgroups';

import SrText from 'client/components/SrText';

export const PROOF_OF_INCOME_PAYTSTUBS = 'paystubs';
export const PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS = 'pinwheelPaystubs';
export const PROOF_OF_INCOME_OFFER_LETTER = 'offerLetter';
export const PROOF_OF_INCOME_BANK_STATEMENTS = 'bankStatements';
export const PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS = 'additionalDocuments';

export const PROOF_OF_INCOME_TYPES = [
  PROOF_OF_INCOME_PAYTSTUBS,
  PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS,
  PROOF_OF_INCOME_OFFER_LETTER,
  PROOF_OF_INCOME_BANK_STATEMENTS,
  PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
];

export const PROOF_OF_INCOME_OPTION_ITEMS = [
  {
    label: 'Paystubs',
    value: PROOF_OF_INCOME_PAYTSTUBS,
  },
  {
    label: 'Pinwheel',
    value: PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS,
  },
  {
    label: 'Offer Letter',
    value: PROOF_OF_INCOME_OFFER_LETTER,
  },
  {
    label: 'Bank Statements',
    value: PROOF_OF_INCOME_BANK_STATEMENTS,
  },
  {
    label: 'Other',
    value: PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
  },
];

export const PROOF_OF_INCOME_TYPE_TO_LABEL_MAPPING = PROOF_OF_INCOME_OPTION_ITEMS.reduce((memo, item) => ({
  [item.value]: item.label,
  ...memo,
}), {});

export const PROOF_OF_INCOME_TO_HOUSEHOLD_APPPLICATION_FIELD_MAPPING = {
  [PROOF_OF_INCOME_PAYTSTUBS]: 'paystubPhotos',
  [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: 'paystubPhotos',
  [PROOF_OF_INCOME_OFFER_LETTER]: 'offerLetterFileUrl',
  [PROOF_OF_INCOME_BANK_STATEMENTS]: 'bankStatementFileUrls',
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: 'additionalProofOfIncomePhotos',
};

export const PROOF_OF_INCOME_TO_ASSEMBLY_LINE_FIELD_GROUP_MAPPING = {
  [PROOF_OF_INCOME_OFFER_LETTER]: [
    'offerLetterCompanyLetterHeadExistsOk',
    'offerLetterCompanyLetterHeadNameOk',
    'offerLetterEmployersName',
    'offerLetterCompensationType',
    'offerLetterSalaryAmount',
    'offerLetterSpecifiesHoursPerWeek',
    'offerLetterHoursPerWeek',
    'offerLetterHourlyWage',
    'offerLetterIssuedDate',
    'offerLetterStartDate',
    'offerLetterMissingInformationFieldIds',
    'offerLetterShouldCountIncomeSource',
    'offerLetterShouldIncludePartnerDocs',
    'offerLetterVerified',
    'offerLetterFileUrl',
  ],
  [PROOF_OF_INCOME_PAYTSTUBS]: [
    'paystubsShouldCountIncomeSource',
    'paystubsShouldIncludePartnerDocs',
    'paystubs',
  ],
  [PROOF_OF_INCOME_BANK_STATEMENTS]: [
    'bankStatementsShouldCountIncomeSource',
    'bankStatementsShouldIncludePartnerDocs',
    'bankStatements',
  ],
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: [
    'additionalProofOfIncomePhotos',
  ],
};

export const RENTAL_APPLICATION_PAYSTUB_FILES_COUNT = 3;
export const RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT = 6;

export const PAYSTUB_MONTHLY_INCOME_MONTHS = 3; // How many months to use when calculating paystub monthly income

export const OFFER_LETTER_COMPENSATION_TYPE_HOURLY = 'hourly';
export const OFFER_LETTER_COMPENSATION_TYPE_SALARY = 'salary';

export const OFFER_LETTER_COMPENSATION_TYPE_OPTION_TYPES = [
  {
    label: 'Salary',
    value: OFFER_LETTER_COMPENSATION_TYPE_SALARY,
  },
  {
    label: 'Hourly',
    value: OFFER_LETTER_COMPENSATION_TYPE_HOURLY,
  },
];

export const PAYROLL_TYPE_DAILY = 'daily';
export const PAYROLL_TYPE_WEEKLY = 'weekly';
export const PAYROLL_TYPE_BI_WEEKLY = 'biWeekly';
export const PAYROLL_TYPE_SEMI_MONTHLY = 'semiMonthly';
export const PAYROLL_TYPE_MONTHLY = 'monthly';
export const PAYROLL_TYPE_YEARLY = 'yearly';

export const ProofOfIncomeWarningMessage = () => (
  <div>
    <SrText weight='medium' display='block' color='red'>READ CAREFULLY!</SrText>
    <SrText weight='medium'>
      {'If your document fails ANY of these checks, '}
      {'it will affect the acceptance of your application and may cause it to be rejected. '}
    </SrText>
    <div style={{ marginTop: 8 }} />
    <SrText weight='medium' display='block'>
      Please check these off before continuing:
    </SrText>
  </div>
);

export function makePoiDocFilePath({ userName, docType, docNumber }) {
  typeCheck('docNumber::Number', docNumber);
  typeCheck('docType::NonEmptyString', docType);

  return `/renter-application-uploads/${slugify(userName || 'noname')}-poi-${docType}-${docNumber}-{uuid}`;
}

export function getProofOfIncomeQuestions({
  currentJobCompany,
  firstName,
  lastName,
  formfieldClassName,
  uploadAs,
}) {
  const userFullName = makeFullName(firstName, lastName);

  const paystubsFields = lodashTimes((index) => {
    const paystubNumber = index + 1;
    const paystubNumberWithSuffix = addNumberPronounSuffix(paystubNumber);
    const moneyIcons = lodashTimes(() => '💵', paystubNumber).join('');
    return {
      id: `paystubPhotos[${index}]`,
      title: (
        `${paystubNumberWithSuffix} Paystub (${paystubNumber} of ${RENTAL_APPLICATION_PAYSTUB_FILES_COUNT})`
      ),
      label:
        index === 0
          ? `${moneyIcons} The landlord wants a photo of your last three paystubs. \n` +
            'Please upload your *most recent paystub* here and others on the next steps. '
          : `${moneyIcons} Please upload your *${paystubNumberWithSuffix} most recent paystub*.`,
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: makePoiDocFilePath({
          userName: userFullName,
          docNumber: paystubNumber,
          docType: PROOF_OF_INCOME_PAYTSTUBS,
        }),
        isPrivate: true,
        uploadAs: uploadAs,
        warning: <ProofOfIncomeWarningMessage />,
        checkmarks: [
          'Is this uploaded document clear and legible?',
          'Does this document clearly state your legal full name?',
          `Does this document clearly show how much you earn before tax is taken out at ${currentJobCompany}?`, // eslint-disable-line max-len
        ],
        className: formfieldClassName,
        minWidth: 800,
        minHeight: 800,
      },
    };
  }, RENTAL_APPLICATION_PAYSTUB_FILES_COUNT);

  const offerLetterFields = [
    {
      id: 'offerLetterFileUrl',
      title: 'Upload the official letterhead of the company.',
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: makePoiDocFilePath({
          userName: userFullName,
          docNumber: 1,
          docType: PROOF_OF_INCOME_OFFER_LETTER,
        }),
        isPrivate: true,
        uploadAs: uploadAs,
        warning: <ProofOfIncomeWarningMessage />,
        checkmarks: [
          'Is this uploaded document clear and legible?',
          'Does this document clearly state your legal full name?',
          `Does this document clearly show how much you earn before tax is taken out at ${currentJobCompany}?`, // eslint-disable-line max-len
        ],
        className: formfieldClassName,
        minWidth: 800,
        minHeight: 800,
      },
    },
  ];

  const bankStatementsFields = lodashTimes((index) => {
    const paystubNumber = index + 1;
    const paystubNumberWithSuffix = addNumberPronounSuffix(paystubNumber);
    const moneyIcons = lodashTimes(() => '💵', paystubNumber).join('');
    return {
      id: `bankStatementFileUrls[${index}]`,
      title: (
        `${paystubNumberWithSuffix} Bank Statement (${paystubNumber} of ` +
        `${RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT})`
      ),
      label:
        index === 0
          ? `${moneyIcons} The landlord wants a photo of your last six months of bank statements. \n` +
            'Please upload your *most recent bank statement* here and others on the next steps. '
          : `${moneyIcons} Please upload your *${paystubNumberWithSuffix} most recent bank statement*.`,
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: makePoiDocFilePath({
          userName: userFullName,
          docNumber: paystubNumber,
          docType: PROOF_OF_INCOME_BANK_STATEMENTS,
        }),
        isPrivate: true,
        uploadAs: uploadAs,
        warning: <ProofOfIncomeWarningMessage />,
        checkmarks: [
          'Is this uploaded document clear and legible?',
          'Does this document clearly state your legal full name?',
          `Does this document clearly show how much you earn before tax is taken out at ${currentJobCompany}?`, // eslint-disable-line max-len
          'Is the bank statement recent within the last 180 days?',
        ],
        className: formfieldClassName,
        minWidth: 800,
        minHeight: 800,
      },
    };
  }, RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT);

  const formFields = [
    ...paystubsFields,
    ...bankStatementsFields,
    ...offerLetterFields,
  ];

  return formFields;
}

export const getPayrollType = (days) => {
  switch (days) {
    case 7: {
      return PAYROLL_TYPE_WEEKLY;
    }
    case 14: {
      return PAYROLL_TYPE_BI_WEEKLY;
    }
    case 15:
    case 16: {
      return PAYROLL_TYPE_SEMI_MONTHLY;
    }
    case 28:
    case 29:
    case 30:
    case 31: {
      return PAYROLL_TYPE_MONTHLY;
    }
  }
  return PAYROLL_TYPE_DAILY;
};

export const getPayrollTypeByDateRange = ({ startDate, endDate }) => {
  const days = getDaysInRange({ startDate, endDate });
  return ({ frequency: getPayrollType(days),
    days,
  });
};

export const calculateMonthlyIncome = ({ grossAmount, payrollType }) => {
  switch (payrollType) {
    case PAYROLL_TYPE_WEEKLY: {
      return (grossAmount * 52) / 12;
    }
    case PAYROLL_TYPE_BI_WEEKLY: {
      return (grossAmount * 26) / 12;
    }
    case PAYROLL_TYPE_SEMI_MONTHLY: {
      return grossAmount * 2;
    }
    case PAYROLL_TYPE_MONTHLY: {
      return grossAmount;
    }
    case PAYROLL_TYPE_YEARLY: {
      return grossAmount / 12;
    }
  }
  return 0;
};

const calculateMonthlyIncomeFromDays = ({ grossAmount, days }) => {
  return roundTwoDecimals((30.437 / days) * grossAmount);
};

const getDaysInRange = ({ startDate, endDate }) => {
  return Math.abs(moment(startDate).diff(endDate, 'days')) + 1;
};

export const calculatePaystubsGrossMonthlyIncomeRaw = (paystubs) => {
  const grossMonthlyIncomeRaw = (paystubs || []).map((paystub) => {
    const hasMissingPaystubData =
        !paystub
        || !paystub.payPeriodStartDate
        || !paystub.payPeriodEndDate;

    const payrollType = hasMissingPaystubData ? null : getPayrollTypeByDateRange({
      startDate: paystub.payPeriodStartDate,
      endDate: paystub.payPeriodEndDate,
    });

    let monthlyIncome;

    if (hasMissingPaystubData || !paystub.grossAmount) {
      monthlyIncome = 0;
    }
    else if (payrollType.frequency === PAYROLL_TYPE_DAILY) {
      monthlyIncome = calculateMonthlyIncomeFromDays({
        grossAmount: paystub.grossAmount,
        days: getDaysInRange({
          startDate: paystub.payPeriodStartDate,
          endDate: paystub.payPeriodEndDate,
        }),
      });
    }
    else {
      monthlyIncome = calculateMonthlyIncome({
        grossAmount: paystub.grossAmount,
        payrollType: payrollType.frequency,
      });
    }
    return ({
      monthlyIncome,
      payrollType,
    });
  }, (paystubs || []).length);

  return grossMonthlyIncomeRaw;
};

export const calculatePaystubsGrossMonthlyIncome = (paystubs) => {
  if (!paystubs) {
    return 0;
  }
  const totalGrossMonthlyIncomeRaw = paystubs
    .reduce((memo, index) => {
      return memo + index.monthlyIncome;
    }, 0) / (paystubs.length);

  const grossMonthlyIncome = isNaN(totalGrossMonthlyIncomeRaw)
    ? 0
    : roundTwoDecimals(totalGrossMonthlyIncomeRaw);

  return grossMonthlyIncome;
};

export const calculateOfferLetterGrossMonthlyIncome = ({
  offerLetterCompensationType,
  offerLetterSalaryAmount,
  offerLetterHoursPerWeek,
  offerLetterHourlyWage,
}) => {
  if (!offerLetterCompensationType) {
    return 0;
  }

  switch (offerLetterCompensationType) {
    case OFFER_LETTER_COMPENSATION_TYPE_HOURLY: {
      return calculateMonthlyIncome({
        grossAmount: offerLetterHourlyWage * offerLetterHoursPerWeek,
        payrollType: PAYROLL_TYPE_WEEKLY,
      });
    }
    case OFFER_LETTER_COMPENSATION_TYPE_SALARY: {
      if (!offerLetterSalaryAmount) {
        return 0;
      }
      return roundTwoDecimals(offerLetterSalaryAmount / 12);
    }
  }
};

export const calculateBankStatementsGrossMonthlyIncome = (bankStatements) => {
  return lodashMean(bankStatements.map(({ depositAmount }) => depositAmount)) || 0;
};

export const calculateAdditionalProofOfIncomeMonthlyIncome = (additionalDocuments, includeAll = false) => {
  const monthlyIncome = additionalDocuments.reduce((memo, additionalDocument) => {
    if (!additionalDocument.shouldCountIncomeSource && !includeAll) {
      return memo;
    }
    return memo + calculateMonthlyIncome({
      grossAmount: additionalDocument.grossAmount,
      payrollType: additionalDocument.payrollType,
    });
  }, 0);

  return monthlyIncome;
};

export const calculateGrossMonthlyIncome = ({
  paystubs,
  offerLetter,
  bankStatements,
  additionalProofOfIncomePhotos,
}) => {
  const result = {
    grossMonthlyIncome: 0,
    paystubsMonthlyIncome: 0,
    offerLetterMonthlyIncome: 0,
    bankStatementsMonthlyIncome: 0,
    additionalProofOfMonthlyIncome: 0,
    additionalProofOfMonthlyIncomeTotal: 0,
  };

  if (paystubs) {
    const paystubsRaw = calculatePaystubsGrossMonthlyIncomeRaw(paystubs);
    result.paystubsMonthlyIncome = calculatePaystubsGrossMonthlyIncome(paystubsRaw);
    result.grossMonthlyIncome += result.paystubsMonthlyIncome;
  }
  if (offerLetter) {
    result.offerLetterMonthlyIncome = calculateOfferLetterGrossMonthlyIncome(offerLetter);
    result.grossMonthlyIncome += result.offerLetterMonthlyIncome;
  }
  if (bankStatements) {
    result.bankStatementsMonthlyIncome = calculateBankStatementsGrossMonthlyIncome(bankStatements);
    result.grossMonthlyIncome += result.bankStatementsMonthlyIncome;
  }

  if (additionalProofOfIncomePhotos) {
    result.additionalProofOfMonthlyIncome = calculateAdditionalProofOfIncomeMonthlyIncome(additionalProofOfIncomePhotos); // eslint-disable-line max-len
    result.additionalProofOfMonthlyIncomeTotal = calculateAdditionalProofOfIncomeMonthlyIncome(additionalProofOfIncomePhotos, true); // eslint-disable-line max-len
    result.grossMonthlyIncome += result.additionalProofOfMonthlyIncome;
  }

  return result;
};

export const calculateAssemblyLineGrossMonthlyIncome = (assemblyLineApplication) => {
  typeCheck('assemblyLineApplication::NonEmptyObject', assemblyLineApplication);

  return calculateGrossMonthlyIncome({
    additionalProofOfIncomePhotos: assemblyLineApplication.additionalProofOfIncomePhotos,
    paystubs: assemblyLineApplication.paystubsShouldCountIncomeSource
      ? assemblyLineApplication.paystubs
      : null,
    offerLetter: assemblyLineApplication.offerLetterShouldCountIncomeSource
      ? pickAssemblyLineApplicationOfferLetterFields(assemblyLineApplication)
      : null,
    bankStatements: assemblyLineApplication.bankStatementsShouldCountIncomeSource
      ? assemblyLineApplication.bankStatements
      : null,
  });
};

export const getInitialAssemblyLineVOIEFormState = (householdApplication) => {
  typeCheck('householdApplication::NonEmptyObject', householdApplication);

  const assemblyLineApplication = { ...householdApplication.assemblyLineApplication || {} };
  assemblyLineApplication.bankStatements = assemblyLineApplication.bankStatements || [];
  assemblyLineApplication.paystubs = assemblyLineApplication.paystubs || [];
  assemblyLineApplication.additionalProofOfIncomePhotos = assemblyLineApplication.additionalProofOfIncomePhotos || []; // eslint-disable-line max-len

  /* eslint-disable max-len */
  const initialAssemblyLineVOIEFormState = {
    offerLetterCompanyLetterHeadExistsOk: undefinedToNull(assemblyLineApplication.offerLetterCompanyLetterHeadExistsOk),
    offerLetterCompanyLetterHeadNameOk: undefinedToNull(assemblyLineApplication.offerLetterCompanyLetterHeadNameOk),
    offerLetterEmployersName: assemblyLineApplication.offerLetterEmployersName || null,
    offerLetterCompensationType: assemblyLineApplication.offerLetterCompensationType || null,
    offerLetterSalaryAmount: assemblyLineApplication.offerLetterSalaryAmount || null,
    offerLetterSpecifiesHoursPerWeek: undefinedToNull(assemblyLineApplication.offerLetterSpecifiesHoursPerWeek),
    offerLetterHoursPerWeek: assemblyLineApplication.offerLetterHoursPerWeek || null,
    offerLetterHourlyWage: assemblyLineApplication.offerLetterHourlyWage || null,
    offerLetterIssuedDate: assemblyLineApplication.offerLetterIssuedDate || null,
    offerLetterStartDate: assemblyLineApplication.offerLetterStartDate || null,
    offerLetterMissingInformationFieldIds: assemblyLineApplication.offerLetterMissingInformationFieldIds || [],
    offerLetterShouldCountIncomeSource: undefinedToNull(assemblyLineApplication.offerLetterShouldCountIncomeSource),
    offerLetterShouldIncludePartnerDocs: undefinedToNull(assemblyLineApplication.offerLetterShouldIncludePartnerDocs),
    offerLetterVerified: undefinedToNull(assemblyLineApplication.offerLetterVerified),
    offerLetterFileUrl: householdApplication.offerLetterFileUrl,
    paystubsShouldCountIncomeSource: undefinedToNull(assemblyLineApplication.paystubsShouldCountIncomeSource),
    paystubsShouldIncludePartnerDocs: undefinedToNull(assemblyLineApplication.paystubsShouldIncludePartnerDocs),
    bankStatementsShouldCountIncomeSource: undefinedToNull(assemblyLineApplication.bankStatementsShouldCountIncomeSource),
    bankStatementsShouldIncludePartnerDocs: undefinedToNull(assemblyLineApplication.bankStatementsShouldIncludePartnerDocs),
    bankStatements: (householdApplication.bankStatementFileUrls || [])
      .map((bankStatementFileUrl, index) => {
        const bankStatement = assemblyLineApplication.bankStatements
          .find(({ fileUrl }) => fileUrl === bankStatementFileUrl)
          // TODO(@d-oliveros):
          //  This line causes an issue where removing a VOIE file messes up with the document's assembly line state.
          //  However, it also adds support for historical data, where assembly line entries did not have the fileUrl prop.
          //  We should remove this line after ~mid Jan. Also, whenever we remove entries from any of the VOIE files,
          //  we should also sync with the assembly line data to remove data that is not pointing to anything anymore.
            || assemblyLineApplication.bankStatements[index]
            || {};

        return {
          fileUrl: bankStatementFileUrl,
          nameOnBankStatementOk: undefinedToNull(bankStatement.nameOnBankStatementOk),
          depositAmount: undefinedToNull(bankStatement.depositAmount),
          endingBalance: undefinedToNull(bankStatement.endingBalance),
          startDate: undefinedToNull(bankStatement.startDate),
          endDate: undefinedToNull(bankStatement.endDate),
          missingInformationFieldIds: bankStatement.missingInformationFieldIds || [],
        };
      }),
    paystubs: (householdApplication.paystubPhotos || [])
      .map((paystubPhotoUrl, index) => {
        const paystub = assemblyLineApplication.paystubs
          .find(({ fileUrl }) => fileUrl === paystubPhotoUrl)
            || assemblyLineApplication.paystubs[index]
            || {};

        return {
          fileUrl: paystubPhotoUrl,
          nameOnPaystubOk: undefinedToNull(paystub.nameOnPaystubOk),
          payPeriodStartDate: paystub.payPeriodStartDate || null,
          payPeriodEndDate: paystub.payPeriodEndDate || null,
          grossAmount: paystub.grossAmount || null,
          missingInformationFieldIds: paystub.missingInformationFieldIds || [],
        };
      }),
    additionalProofOfIncomePhotos: (householdApplication.additionalProofOfIncomePhotos || [])
      .map((additionalProofOfIncomePhotoUrl, index) => {
        const additionalProofOfIncome = assemblyLineApplication.additionalProofOfIncomePhotos
          .find(({ fileUrl }) => fileUrl === additionalProofOfIncomePhotoUrl)
            || assemblyLineApplication.additionalProofOfIncomePhotos[index]
            || {};

        return {
          fileUrl: additionalProofOfIncomePhotoUrl,
          nameOnDocumentOk: undefinedToNull(additionalProofOfIncome.nameOnDocumentOk),
          grossAmount: additionalProofOfIncome.grossAmount || null,
          payrollType: additionalProofOfIncome.payrollType || null,
          shouldCountIncomeSource: undefinedToNull(additionalProofOfIncome.shouldCountIncomeSource),
          shouldIncludePartnerDocs: undefinedToNull(additionalProofOfIncome.shouldIncludePartnerDocs),
          missingInformationFieldIds: additionalProofOfIncome.missingInformationFieldIds || [],
        };
      }),
  };
  /* eslint-enable max-len */

  return initialAssemblyLineVOIEFormState;
};

export const getAssemblyLineVoieStatuses = ({ assemblyLineVoie, processingTime, householdApplication }) => {
  const stepStatuses = {
    offerLetter: (
      !householdApplication.offerLetterFileUrl
      || (
        typeof assemblyLineVoie.offerLetterShouldCountIncomeSource === 'boolean'
        && !assemblyLineVoie.offerLetterShouldCountIncomeSource
      )
    )
      ? null
      : (
        typeof assemblyLineVoie.offerLetterCompanyLetterHeadExistsOk !== 'boolean'
        && typeof assemblyLineVoie.offerLetterCompanyLetterHeadNameOk !== 'boolean'
        && !assemblyLineVoie.offerLetterEmployersName
        && !assemblyLineVoie.offerLetterCompensationType
        && !assemblyLineVoie.offerLetterSalaryAmount
        && !assemblyLineVoie.offerLetterHourlyWage
        && typeof assemblyLineVoie.offerLetterSpecifiesHoursPerWeek !== 'boolean'
        && !assemblyLineVoie.offerLetterHoursPerWeek
        && !assemblyLineVoie.offerLetterIssuedDate
        && !assemblyLineVoie.offerLetterStartDate
        && typeof assemblyLineVoie.offerLetterVerified !== 'boolean'
        && assemblyLineVoie.offerLetterMissingInformationFieldIds.length === 0
        && typeof assemblyLineVoie.offerLetterShouldCountIncomeSource !== 'boolean'
      )
        ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED
        : (
          assemblyLineVoie.offerLetterCompanyLetterHeadExistsOk
          && assemblyLineVoie.offerLetterCompanyLetterHeadNameOk
          && assemblyLineVoie.offerLetterEmployersName
          && assemblyLineVoie.offerLetterCompensationType
          && (
            assemblyLineVoie.offerLetterCompensationType !== OFFER_LETTER_COMPENSATION_TYPE_SALARY
            || assemblyLineVoie.offerLetterSalaryAmount
          )
          && (
            assemblyLineVoie.offerLetterCompensationType !== OFFER_LETTER_COMPENSATION_TYPE_HOURLY
            || (
              assemblyLineVoie.offerLetterHourlyWage
              && assemblyLineVoie.offerLetterSpecifiesHoursPerWeek
              && assemblyLineVoie.offerLetterHoursPerWeek
            )
          )
          && assemblyLineVoie.offerLetterIssuedDate
          && assemblyLineVoie.offerLetterStartDate
          && assemblyLineVoie.offerLetterVerified
          && assemblyLineVoie.offerLetterMissingInformationFieldIds.length === 0
          && (
            !processingTime?.startDate
            || moment(processingTime.startDate).diff(
              assemblyLineVoie.offerLetterStartDate, 'days'
            ) <= SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF
          )
        )
          ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
          : SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
    paystubs: (
      typeof assemblyLineVoie.paystubsShouldCountIncomeSource === 'boolean'
      && !assemblyLineVoie.paystubsShouldCountIncomeSource
    )
      ? null
      : lodashTimes((index) => {
        const status = !assemblyLineVoie.paystubs[index]
          || (
            typeof assemblyLineVoie.paystubs[index].nameOnPaystubOk !== 'boolean'
              && !assemblyLineVoie.paystubs[index].payPeriodStartDate
              && !assemblyLineVoie.paystubs[index].payPeriodEndDate
              && !assemblyLineVoie.paystubs[index].grossAmount
              && assemblyLineVoie.paystubs[index].missingInformationFieldIds.length === 0
              && typeof assemblyLineVoie.paystubsShouldCountIncomeSource !== 'boolean'
          );
        return status
          ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED
          : (
            assemblyLineVoie.paystubs[index].nameOnPaystubOk
            && assemblyLineVoie.paystubs[index].payPeriodStartDate
            && assemblyLineVoie.paystubs[index].payPeriodEndDate
            && assemblyLineVoie.paystubs[index].grossAmount
            && assemblyLineVoie.paystubs[index].missingInformationFieldIds.length === 0
            && (
              !processingTime?.startDate
              || moment(processingTime.startDate).diff(
                assemblyLineVoie.paystubs[index].payPeriodStartDate, 'days'
              ) <= SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF
            )
          )
            ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
            : SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
      }, (householdApplication.paystubPhotos || []).length),
    bankStatements: (
      typeof assemblyLineVoie.bankStatementsShouldCountIncomeSource === 'boolean'
      && !assemblyLineVoie.bankStatementsShouldCountIncomeSource
    )
      ? null
      : lodashTimes((index) => {
        const status = !assemblyLineVoie.bankStatements[index]
          || (
            typeof assemblyLineVoie.bankStatements[index].nameOnBankStatementOk !== 'boolean'
            && !assemblyLineVoie.bankStatements[index].depositAmount
            && !assemblyLineVoie.bankStatements[index].endingBalance
            && !assemblyLineVoie.bankStatements[index].startDate
            && !assemblyLineVoie.bankStatements[index].endDate
            && assemblyLineVoie.bankStatements[index].missingInformationFieldIds.length === 0
            && typeof assemblyLineVoie.bankStatementsShouldCountIncomeSource !== 'boolean'
          );

        return status
          ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED
          : (
            assemblyLineVoie.bankStatements[index].nameOnBankStatementOk
            && assemblyLineVoie.bankStatements[index].depositAmount
            && assemblyLineVoie.bankStatements[index].endingBalance
            && assemblyLineVoie.bankStatements[index].startDate
            && assemblyLineVoie.bankStatements[index].endDate
            && assemblyLineVoie.bankStatements[index].missingInformationFieldIds.length === 0
            && (
              !processingTime.startDate
              || moment(processingTime.startDate).diff(
                assemblyLineVoie.bankStatements[index].startDate, 'days'
              ) <= SR_APPLICATION_ASSEMBLY_LINE_INCOME_MAX_DAYS_DIFF
            )
          )
            ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
            : SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
      }, (householdApplication.bankStatementFileUrls || []).length),
    additionalProofOfIncomePhotos:
      lodashTimes((index) => {
        if (
          assemblyLineVoie.additionalProofOfIncomePhotos[index]
          && typeof assemblyLineVoie.additionalProofOfIncomePhotos[index].shouldCountIncomeSource === 'boolean' // eslint-disable-line max-len
          && !assemblyLineVoie.additionalProofOfIncomePhotos[index].shouldCountIncomeSource
        ) {
          return null;
        }
        const status = !assemblyLineVoie.additionalProofOfIncomePhotos[index]
          || (
            typeof assemblyLineVoie.additionalProofOfIncomePhotos[index].nameOnDocumentOk !== 'boolean'
            && !assemblyLineVoie.additionalProofOfIncomePhotos[index].grossAmount
            && !assemblyLineVoie.additionalProofOfIncomePhotos[index].payrollType
            && assemblyLineVoie.additionalProofOfIncomePhotos[index].missingInformationFieldIds.length === 0
            && typeof assemblyLineVoie.additionalProofOfIncomePhotos[index].shouldCountIncomeSource !== 'boolean' // eslint-disable-line max-len
          );

        return status
          ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED
          : (
            assemblyLineVoie.additionalProofOfIncomePhotos[index].nameOnDocumentOk
            && assemblyLineVoie.additionalProofOfIncomePhotos[index].grossAmount
            && assemblyLineVoie.additionalProofOfIncomePhotos[index].payrollType
            && assemblyLineVoie.additionalProofOfIncomePhotos[index].missingInformationFieldIds.length === 0
          )
            ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
            : SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
      }, (householdApplication.additionalProofOfIncomePhotos || []).length),
  };

  stepStatuses.status = getLatestStatus(lodashValues(stepStatuses));

  if (
    typeof householdApplication.hasSourceOfIncome === 'boolean'
    && !householdApplication.hasSourceOfIncome
  ) {
    stepStatuses.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
  }

  return stepStatuses;
};

export const getVoieStartEndDates = ({ assemblyLineApplication, householdApplication }) => {
  const nowIsoString = new Date().toISOString();

  const stepStatuses = getAssemblyLineVoieStatuses({
    assemblyLineVoie: assemblyLineApplication,
    processingTime: assemblyLineApplication?.processingTime || {},
    householdApplication,
  });

  const isVoieCompleted = stepStatuses.status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
  const isVoieStarted = (
    isVoieCompleted
    || stepStatuses.status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS
  );

  return {
    voieStartedDate: assemblyLineApplication.voieStartedDate || (isVoieStarted ? nowIsoString : null),
    voieCompletedDate: assemblyLineApplication.voieCompletedDate || (isVoieCompleted ? nowIsoString : null),
  };
};
